'use client';

import { useState, useEffect } from 'react';
import { Copy, Check, AlertCircle, Maximize2, Minimize2, Type } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { markdownFormatterContent } from '../../data/markdown-formatter-content';

export default function MarkdownFormatter() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);
    const [isPreview, setIsPreview] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [tabSize, setTabSize] = useState(2);

    const sampleMarkdown = `# Markdown Formatter

## Format and Preview Markdown

This is a **Markdown** formatter. Type or paste your Markdown on the left and see the formatted output on the right.

### Features
- Live preview
- Syntax highlighting
- Copy to clipboard
- Toggle fullscreen mode

\`\`\`javascript
// Example code block
function helloWorld() {
  console.log('Hello, Markdown!');
}
\`\`\`

> Blockquotes are also supported

[Visit our website](https://example.com)`;

    // Load sample markdown or localStorage on mount
    useEffect(() => {
        const savedInput = localStorage.getItem('markdownFormatterInput');
        setInput(savedInput || sampleMarkdown);
    }, []);

    // Parse Markdown whenever input changes
    useEffect(() => {
        const parseMarkdown = async () => {
            try {
                const html = await marked.parse(input); // html is string
                const cleanHtml = DOMPurify.sanitize(html);
                setOutput(cleanHtml);
                setError('');
                localStorage.setItem('markdownFormatterInput', input);
            } catch (err) {
                setError('Error formatting Markdown: ' + (err as Error).message);
            }
        };

        parseMarkdown();
    }, [input]);


    const copyToClipboard = () => {
        if (!input) return;
        navigator.clipboard.writeText(input).then(() => {
            setCopied(true);
            toast.success('Copied to clipboard!');
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const clearAll = () => {
        setInput('');
        setError('');
        localStorage.removeItem('markdownFormatterInput');
    };

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    const handleTabKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Tab') {
            e.preventDefault();
            const { selectionStart, selectionEnd } = e.currentTarget;
            const newValue =
                input.substring(0, selectionStart) +
                ' '.repeat(tabSize) +
                input.substring(selectionEnd);
            setInput(newValue);
            const newCursorPos = selectionStart + tabSize;
            setTimeout(() => {
                e.currentTarget.setSelectionRange(newCursorPos, newCursorPos);
            }, 0);
        }
    };

    return (
        <div className={`max-w-7xl mx-auto p-4 md:p-6 ${isFullscreen ? 'fixed inset-0 bg-white z-50 p-0' : ''}`}>
            <div className={`mb-6 ${isFullscreen ? 'p-6' : ''}`}>
                <div className="flex justify-between items-center mb-2">
                    <ToolPageIntro title="Markdown Formatter">
                Format and beautify Markdown text with proper syntax and structure. Free online Markdown formatter.
            </ToolPageIntro>

                    <div className="flex items-center space-x-2">
                        <button
                            onClick={toggleFullscreen}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-full"
                            title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                        >
                            {isFullscreen ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
                        </button>
                    </div>
                </div>
                <p className="text-gray-600">
                    Format and preview your Markdown in real-time. Supports all standard Markdown syntax.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Markdown Input */}
                <div className="space-y-2">
                    <div className="flex justify-between items-center">
                        <label htmlFor="input" className="block text-sm font-medium text-gray-700">
                            Markdown Input
                        </label>
                        <div className="flex space-x-2">
                            <button
                                onClick={clearAll}
                                className="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 transition-colors"
                            >
                                Clear All
                            </button>
                            <button
                                onClick={copyToClipboard}
                                disabled={!input}
                                className={`px-3 py-1.5 text-sm rounded transition-colors ${input ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    }`}
                            >
                                {copied ? 'Copied!' : 'Copy'}
                            </button>
                        </div>
                    </div>
                    <textarea
                        id="input"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleTabKey}
                        className="w-full h-[calc(100vh-300px)] min-h-[400px] font-mono text-sm p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                        placeholder="# Type or paste your Markdown here...\n## Start writing to see the preview update in real-time"
                        spellCheck="false"
                    />
                    <div className="text-xs text-gray-500 flex justify-between">
                        <span>Tip: Press Tab to insert {tabSize} spaces</span>
                        <label className="flex items-center">
                            <span className="mr-2">Tab size:</span>
                            <select
                                value={tabSize}
                                onChange={(e) => setTabSize(Number(e.target.value))}
                                className="text-xs border border-gray-300 rounded px-1 py-0.5"
                            >
                                <option value={2}>2 spaces</option>
                                <option value={4}>4 spaces</option>
                            </select>
                        </label>
                    </div>
                </div>

                {/* Preview / HTML Output */}
                <div className="space-y-2">
                    <div className="flex justify-between items-center">
                        <label className="block text-sm font-medium text-gray-700">{isPreview ? 'Preview' : 'HTML Output'}</label>
                        <div className="flex space-x-2">
                            <button
                                onClick={() => setIsPreview(!isPreview)}
                                className="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 transition-colors"
                            >
                                {isPreview ? 'Show HTML' : 'Show Preview'}
                            </button>
                            <button
                                onClick={() => {
                                    if (output) {
                                        navigator.clipboard.writeText(isPreview ? output : input);
                                        toast.success('Copied to clipboard!');
                                    }
                                }}
                                disabled={!output}
                                className={`p-1.5 rounded ${output ? 'text-gray-600 hover:bg-gray-100 hover:text-gray-900' : 'text-gray-300 cursor-not-allowed'
                                    }`}
                                title="Copy to clipboard"
                            >
                                <Copy className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                    <div className="relative">
                        {isPreview ? (
                            <div
                                className="prose max-w-none h-[calc(100vh-300px)] min-h-[400px] p-4 border border-gray-300 rounded-lg overflow-auto"
                                dangerouslySetInnerHTML={{ __html: output }}
                            />
                        ) : (
                            <pre className="h-[calc(100vh-300px)] min-h-[400px] p-4 bg-gray-50 border border-gray-300 rounded-lg overflow-auto text-sm">
                                <code className="text-gray-800">{output || 'Formatted output will appear here...'}</code>
                            </pre>
                        )}
                        {error && (
                            <div className="absolute bottom-2 left-2 right-2 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm flex items-start">
                                <AlertCircle className="h-4 w-4 mr-1.5 mt-0.5 flex-shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Markdown Cheat Sheet */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                <h3 className="font-medium text-blue-800 mb-2">Markdown Cheat Sheet</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-blue-700">
                    <div>
                        <h4 className="font-medium mb-1">Headers</h4>
                        <pre className="bg-white p-1 rounded text-xs"># H1<br />## H2<br />### H3</pre>
                    </div>
                    <div>
                        <h4 className="font-medium mb-1">Emphasis</h4>
                        <pre className="bg-white p-1 rounded text-xs">
                            *italic* or _italic_<br />
                            **bold** or __bold__<br />
                            ~~strikethrough~~
                        </pre>
                    </div>
                    <div>
                        <h4 className="font-medium mb-1">Lists</h4>
                        <pre className="bg-white p-1 rounded text-xs">
                            - Item 1<br />
                            - Item 2<br />
                            &nbsp;&nbsp;- Nested
                        </pre>
                    </div>
                    <div>
                        <h4 className="font-medium mb-1">Code</h4>
                        <pre className="bg-white p-1 rounded text-xs">
                            `code`<br />
                            ```<br />
                            code block<br />
                            ```
                        </pre>
                    </div>
                </div>
            </div>

            {/* SEO CONTENT */}
            <div className="mt-16">
                <InformationalContent
                    title={markdownFormatterContent.title}
                    description={markdownFormatterContent.description}
                    content={markdownFormatterContent.content}
                    faqs={markdownFormatterContent.faqs}
                    relatedTools={markdownFormatterContent.relatedTools}
                />
            </div>
        </div>
    );
}
